//src/pages/inventory/PurchaseInvoice/AddNewPurchaseInvoice.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  IconButton,
  Divider,
  InputAdornment,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  FormControl,
  FormHelperText
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  InvoiceSummarySection,
  calcManualSgstCgstTotal,
  resolveAttachmentUrl,
} from "../../../components/InvoiceFormShared";
import {
  NO_INNER_SCROLL_FORM_MODAL_SX,
  COMPACT_FORM_MODAL_SX,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../../utils/formModalStyles";
import { createInventory, updateInventory, getExistingBatchNumbers } from "../../../services/inventoryService";
import { recordPathologyStockIn } from "../../../services/pathologyTransactionService";
import { getBOMsFromBaseTable } from "../../../services/labService";
import { createInvoice } from "../../../services/invoiceService";
import { uploadAttachment } from "../../../services/uploadService";
import { useSelector } from 'react-redux';
import { getOrganizationName, getOrganizationId, fetchAndStoreOrganizationName } from "../../../utils/organizationUtils";
import { createSupplier, searchSuppliers, getAllSuppliers } from "../../../services/supplierService";
import { getActiveSubscription } from "../../../services/subscriptionHistoryService";
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Link } from '@mui/material';
import {
  calculatePathStockFromPackages,
  getBomPackageCost,
  getPathStockQuantityCaption,
  getPathStockUnitPriceCaption,
  parseDecimalNumber,
} from "../../../utils/pathItemPricingUtils";
import { resolvePathCriticalLevel } from "../../../utils/pathLowStock";
import StockAddInvoiceSupplierSection, {
  ADD_NEW_SUPPLIER_VALUE,
} from "../../../components/StockAddInvoiceSupplierSection";

/** Allow decimal amounts like 259.82 while typing (e.g. "259."). */
const isDecimalAmountInput = (raw) =>
  raw === "" || /^\d*\.?\d*$/.test(String(raw));

const roundMoney2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const resolvePathSystemLinePrice = (entry, calculatedSystemPrice) => {
  if (
    entry?.systemPriceOverride !== "" &&
    entry?.systemPriceOverride !== null &&
    entry?.systemPriceOverride !== undefined
  ) {
    return parseDecimalNumber(entry.systemPriceOverride, 0);
  }
  return Number.isFinite(calculatedSystemPrice) ? calculatedSystemPrice : 0;
};

const validationSchema = Yup.object({
  invoiceNumber: Yup.string().required("Invoice Number is required"),
  totalCost: Yup.number().nullable().notRequired(),
  invoiceDate: Yup.date()
    .nullable()
    .typeError("Invoice Date is required")
    .required("Invoice Date is required"),
  invoiceType: Yup.string().required("Invoice Type is required"),
  supplierName: Yup.string().required("Supplier Name is required"),
  supplierContact: Yup.string().nullable(),
  supplierAddress: Yup.string().nullable(),
  supplierGstNumber: Yup.string().nullable(),
  attachment: Yup.string()
    .nullable()
    .required("Attachment is required")
    .trim()
    .min(1, "Attachment is required"),
  totalDiscount: Yup.mixed().nullable().notRequired(),
  discount: Yup.mixed().nullable().notRequired(),
  sgst: Yup.mixed().nullable().notRequired(),
  cgst: Yup.mixed().nullable().notRequired(),
});

const productEntryValidationSchema = Yup.object().shape({
  name: Yup.string().required("Product name is required"),
  batchNumber: Yup.string().required("Batch number is required"),
  quantity: Yup.number().required("Quantity is required").min(0, "Quantity must be at least 0"),
  price: Yup.number()
    .required("Price is required")
    .moreThan(0, "Price must be greater than 0"),
  vendorPrice: Yup.number()
    .required("Supplier price is required")
    .moreThan(0, "Supplier price must be greater than 0"),
  expirationDate: Yup.mixed()
    .nullable()
    .test("is-valid-date", "Expiration date is required", function(value) {
      if (value === null || value === undefined || value === '') {
        return this.createError({ message: "Expiration date is required" });
      }
      return true;
    }),
});

const AddNewPurchaseInvoice = ({ open, onClose, product_info, refreshData }) => {
  const [bomList, setBOMList] = useState([]);
  const [existingProductNames, setExistingProductNames] = useState([]);
  const [showBatchNumbersModal, setShowBatchNumbersModal] = useState(false);
  const [selectedEntryIndex, setSelectedEntryIndex] = useState(null);
  
  // State to track touched fields for product entries
  const [touchedProductEntries, setTouchedProductEntries] = useState([{
    expirationDate: false
  }]);
  
  // State to track validation errors for product entries
  const [productEntryErrors, setProductEntryErrors] = useState([]);
  
  // Force re-render trigger for errors
  const [errorUpdateTrigger, setErrorUpdateTrigger] = useState(0);
  const [suppliersList, setSuppliersList] = useState([]);
  const [selectedSupplierKey, setSelectedSupplierKey] = useState("");
  const [isNewSupplier, setIsNewSupplier] = useState(false);

  const [productEntries, setProductEntries] = useState([{
    name: "",
    batchNumber: "",
    quantity: 0,
    price: 0,
    systemPriceOverride: null, // null = auto (package cost × qty); set when user edits
    vendorPrice: "",
    expirationDate: null,
    criticalLevel: 10,
    existingProductNames: [],
    existingBatchNumbers: [] // Ensure this is initialized as an empty array
  }]);

  const user = useSelector((state) => state.user.user);
  const [organizationName, setOrganizationName] = useState("Aryan Hospital"); // State to hold organization name, default to "Aryan Hospital"
  const [gstPercentage, setGstPercentage] = useState(null); // State to hold GST percentage from subscription
  const [attachment, setAttachment] = useState(null);

  const formik = useFormik({
    initialValues: {
    invoiceNumber: "",
    quantity: 0,
    price: 0,
    vendorPrice: 0,
    totalCost: 0,
    invoiceDate: null,
    attachment: "",
    invoiceType: "",
    actualCost: 0,
    discount: 0,
    totalDiscount: "",
    sgst: "",
    cgst: "",
    supplierName: "",
    supplierAddress: "",
    supplierContact: "",
    supplierGstNumber: "",
    payment_mode: { mode: "cash" },
    particulars: "",
  },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setTouched: setFormikTouched }) => {
      try {
        // Step 1: Mark all form fields as touched to show errors
        setFormikTouched({
          invoiceNumber: true,
          invoiceDate: true,
          invoiceType: true,
          totalCost: true,
          supplierName: true,
          supplierContact: true,
          supplierAddress: true,
          supplierGstNumber: true,
          attachment: true,
        }, false);
        
        // Step 2: Mark all expiry dates as touched
        const newTouched = productEntries.map(() => ({ expirationDate: true }));
        setTouchedProductEntries(newTouched);
        
        // Step 3: Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Step 4: Validate formik form explicitly
        let formErrors = {};
        try {
          await formik.validateForm();
          formErrors = { ...formik.errors };
        } catch (error) {
          formErrors = { ...formik.errors };
        }
        
        // Step 5: Check for expiry date errors
        const expirationDateErrors = [];
        productEntries.forEach((entry, idx) => {
          if (!entry.expirationDate) {
            expirationDateErrors.push(idx);
          }
        });
        
        // Step 6: Set expiry date errors if any
        if (expirationDateErrors.length > 0) {
          const updatedErrors = [...productEntryErrors];
          expirationDateErrors.forEach(idx => {
            if (!updatedErrors[idx]) {
              updatedErrors[idx] = {};
            }
            updatedErrors[idx].expirationDate = "Expiration date is required";
          });
          setProductEntryErrors(updatedErrors);
          setErrorUpdateTrigger(prev => prev + 1);
        }
        
        // Step 7: Final validation check - use direct value checks
        const finalFormErrors = {};
        let hasErrors = false;
        
        // Check invoice number
        if (!values.invoiceNumber || String(values.invoiceNumber).trim() === '') {
          finalFormErrors.invoiceNumber = "Invoice Number is required";
          hasErrors = true;
        }
        
        // Check invoice date
        const invoiceDateToCheck = formik.values.invoiceDate || values.invoiceDate;
        const hasInvoiceDate = invoiceDateToCheck !== null && 
                              invoiceDateToCheck !== undefined && 
                              invoiceDateToCheck !== '';
        
        if (!hasInvoiceDate) {
          finalFormErrors.invoiceDate = "Invoice Date is required";
          hasErrors = true;
        } else {
          formik.setFieldError("invoiceDate", undefined);
        }
        
        // Check invoice type
        if (!values.invoiceType || String(values.invoiceType).trim() === '') {
          finalFormErrors.invoiceType = "Invoice Type is required";
          hasErrors = true;
        }
        
        // Soft-check total from supplier prices (exact total set after tax calc)
        const previewSupplierTotal = productEntries.reduce((sum, entry, index) => {
          const supplierAmount =
            parseFloat(
              formik.values[`vendorPrice-${index}`] ?? entry.vendorPrice
            ) || 0;
          return sum + supplierAmount;
        }, 0);
        if (previewSupplierTotal <= 0) {
          finalFormErrors.totalCost =
            "Total Cost must be greater than 0. Please add products with supplier price.";
          hasErrors = true;
        } else {
          formik.setFieldError("totalCost", undefined);
        }
        
        // Attachment is mandatory for add stock (must be a selected file)
        if (!(attachment instanceof File)) {
          finalFormErrors.attachment = "Attachment is required";
          hasErrors = true;
        }

        // Check supplier name
        if (!selectedSupplierKey) {
          finalFormErrors.supplierName = "Please select a supplier or add a new one";
          hasErrors = true;
        } else if (!values.supplierName || String(values.supplierName).trim() === '') {
          finalFormErrors.supplierName = "Supplier Name is required";
          hasErrors = true;
        }
        
        // New supplier: require contact / address / GST; existing: details are read-only
        if (isNewSupplier || selectedSupplierKey === ADD_NEW_SUPPLIER_VALUE) {
          if (!values.supplierContact || String(values.supplierContact).trim() === '') {
            finalFormErrors.supplierContact = "Supplier Contact is required";
            hasErrors = true;
          } else {
            const digitsOnly = String(values.supplierContact).replace(/\D/g, '');
            if (digitsOnly.length !== 10) {
              finalFormErrors.supplierContact = "Supplier Contact must be exactly 10 digits";
              hasErrors = true;
            }
          }
          
          if (!values.supplierAddress || String(values.supplierAddress).trim() === '') {
            finalFormErrors.supplierAddress = "Supplier Address is required";
            hasErrors = true;
          }
          
          if (!values.supplierGstNumber || String(values.supplierGstNumber).trim() === '') {
            finalFormErrors.supplierGstNumber = "GST Number is required";
            hasErrors = true;
          }
        }
        
        // Step 8: Check for product entry errors - validate each entry
        const productValidationErrors = [];
        productEntries.forEach((entry, idx) => {
          const entryErrors = {};
          
          if (!entry.name || String(entry.name).trim() === '') {
            entryErrors.name = "Product name is required";
            hasErrors = true;
          }
          
          if (!entry.batchNumber || String(entry.batchNumber).trim() === '') {
            entryErrors.batchNumber = "Batch number is required";
            hasErrors = true;
          }
          
          if (!entry.expirationDate) {
            entryErrors.expirationDate = "Expiration date is required";
            hasErrors = true;
          }
          
          if (!entry.quantity || parseFloat(entry.quantity) <= 0) {
            entryErrors.quantity = "Quantity is required and must be greater than 0";
            hasErrors = true;
          }
          
          const packageQty = parseDecimalNumber(
            formik.values[`quantity-${idx}`] ?? entry.quantity,
            0
          );
          const packageCost = parseDecimalNumber(
            entry.packageCost ?? entry.price,
            0
          );
          const calculatedSystemPrice = roundMoney2(packageCost * packageQty);
          if (!(resolvePathSystemLinePrice(entry, calculatedSystemPrice) > 0)) {
            entryErrors.price = "System price is required and must be greater than 0";
            hasErrors = true;
          }
          
          if (entry.vendorPrice === '' || entry.vendorPrice === null || entry.vendorPrice === undefined || !(parseDecimalNumber(entry.vendorPrice) > 0)) {
            entryErrors.vendorPrice = "Supplier price is required and must be greater than 0";
            hasErrors = true;
          }
          
          if (Object.keys(entryErrors).length > 0) {
            productValidationErrors[idx] = entryErrors;
          }
        });
        
        // Step 9: If there are errors, stop submission and show them
        if (hasErrors) {
          // Set form errors
          Object.keys(finalFormErrors).forEach(key => {
            formik.setFieldError(key, finalFormErrors[key]);
          });
          
          // Set product entry errors
          if (productValidationErrors.length > 0) {
            setProductEntryErrors(productValidationErrors);
          }
          
          // Force re-render to show errors
          setErrorUpdateTrigger(prev => prev + 1);
          return;
        }

        // Validate each product entry with Yup
        for (const entry of productEntries) {
          await productEntryValidationSchema.validate(entry, { abortEarly: false });
        }

        // Calculate totals from supplier price (same as medicine add stock)
        const supplierTotal = productEntries.reduce((sum, entry, index) => {
          const supplierAmount =
            parseFloat(
              formik.values[`vendorPrice-${index}`] ?? entry.vendorPrice
            ) || 0;
          return sum + supplierAmount;
        }, 0);
        const discount =
          parseFloat(values.totalDiscount ?? values.discount) || 0;
        const amountAfterDiscount = supplierTotal - discount;
        const sgst = Math.round((parseFloat(values.sgst) || 0) * 100) / 100;
        const cgst = Math.round((parseFloat(values.cgst) || 0) * 100) / 100;
        const amount = amountAfterDiscount;
        const totalAmount = amountAfterDiscount + sgst + cgst;
        formik.setFieldValue("actualCost", supplierTotal, false);
        formik.setFieldValue("discount", discount, false);
        formik.setFieldValue("sgst", sgst, false);
        formik.setFieldValue("cgst", cgst, false);
        formik.setFieldValue("totalCost", totalAmount, false);

        // Step: Handle supplier data - use selected existing supplier or create new
        let supplierId = null;
        if (
          selectedSupplierKey &&
          selectedSupplierKey !== ADD_NEW_SUPPLIER_VALUE &&
          !isNewSupplier
        ) {
          supplierId = Number(selectedSupplierKey) || selectedSupplierKey;
          console.log(`[AddNewPurchaseInvoice] ✅ Using selected supplier ID: ${supplierId}`);
        } else if (values.supplierName && values.supplierName.trim()) {
          try {
            const organizationId = getOrganizationId(user);
            const existingSuppliers = await searchSuppliers(values.supplierName.trim());
            
            const matchingSupplier = existingSuppliers.find(
              (s) => (s.name || '').toLowerCase().trim() === values.supplierName.toLowerCase().trim()
            );
            
            if (matchingSupplier) {
              supplierId = matchingSupplier.id;
              console.log(`[AddNewPurchaseInvoice] ✅ Found existing supplier: ${values.supplierName} (ID: ${supplierId})`);
            } else {
              const supplierData = {
                name: values.supplierName.trim(),
                address: values.supplierAddress?.trim() || null,
                contact: values.supplierContact?.trim() || null,
                gstNumber: values.supplierGstNumber?.trim() || null,
              };
              
              if (organizationId && organizationId > 0) {
                supplierData.organizationId = organizationId;
              }
              
              console.log(`[AddNewPurchaseInvoice] Creating new supplier: ${values.supplierName}...`);
              const createdSupplier = await createSupplier(supplierData);
              supplierId = createdSupplier.id;
              console.log(`[AddNewPurchaseInvoice] ✅ Created new supplier: ${values.supplierName} (ID: ${supplierId})`);
            }
          } catch (supplierError) {
            console.error(`[AddNewPurchaseInvoice] Error handling supplier:`, supplierError);
            console.warn(`[AddNewPurchaseInvoice] Continuing without supplier ID`);
          }
        }

        // Create an invoice first
        let formattedInvoiceDate = null;
        if (values.invoiceDate) {
          try {
            formattedInvoiceDate = dayjs(values.invoiceDate).format("YYYY-MM-DD");
          } catch (error) {
            console.error("Error formatting invoice date:", error);
            throw new Error("Invalid invoice date");
          }
        }
        
        // Incoming stock invoice: supplier is the "from" party
        const fromName = values.supplierName?.trim() || organizationName || 'Aryan Hospital';

        let attachmentUrl = null;
        if (attachment instanceof File) {
          const uploaded = await uploadAttachment(attachment);
          attachmentUrl = resolveAttachmentUrl(uploaded);
        }
        
        const invoiceData = {
          invoiceNumber: values.invoiceNumber,
          amount: amount, // Set the amount
          sgst: sgst, // Set SGST
          cgst: cgst, // Set CGST
          totalAmount: totalAmount, // Set total amount
          date: formattedInvoiceDate,
          invoiceType: values.invoiceType,
          fromName: fromName,
          supplierId: supplierId || null,
          supplierName: values.supplierName,
          supplierAddress: values.supplierAddress,
          supplierContact: values.supplierContact,
          supplierGstNumber: values.supplierGstNumber,
          discount: discount,
          paymentMode: (values.payment_mode?.mode || "cash").toUpperCase(),
          creditOrganizationId:
            values.payment_mode?.mode === "credit"
              ? values.payment_mode?.organizationId || null
              : null,
          particulars: values.particulars?.trim?.() || "",
          ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
        };
      const createdInvoice = await createInvoice(invoiceData); // Create the invoice and get the ID

        const req_data = {
          ...values,
          userId: user.id,
          productEntries: productEntries.map((entry, index) => {
            const packageQty = resolveEntryPackageQty(entry, index);
            const bom = bomList.find((b) => b.id === entry.bomId);
            const packageCost = resolveEntryPackageCost(entry, bom);
            const calculatedSystemPrice =
              Math.round(packageCost * packageQty * 100) / 100;
            const systemLinePrice = roundMoney2(
              resolvePathSystemLinePrice(entry, calculatedSystemPrice)
            );
            return {
              ...entry,
              quantity: packageQty,
              price: packageCost,
              packageCost,
              vendorPrice: parseDecimalNumber(
                formik.values[`vendorPrice-${index}`] ?? entry.vendorPrice,
                0
              ),
              expirationDate: entry.expirationDate
                ? dayjs(entry.expirationDate).format("YYYY-MM-DD")
                : null,
              finalPrice: systemLinePrice,
              invoiceId: createdInvoice.id,
              bomId: entry.bomId,
            };
          }),
        };

        // Create product inventory records
        const productInventoryPromises = req_data.productEntries.map(async (entry, index) => {
          const packageQty = resolveEntryPackageQty(entry, index);
          const bom = bomList.find((b) => b.id === entry.bomId);
          // Always use path-item package cost (202), never a pre-multiplied total
          const packageCost = resolveEntryPackageCost(entry, bom);
          const stockCalc = bom
            ? calculatePathStockFromPackages(packageQty, { ...bom, cost: packageCost })
            : {
                quantity: packageQty,
                price: Math.round(packageCost * packageQty * 100) / 100,
              };
          // Prefer mapped finalPrice (user-edited System Price with decimals, e.g. 259.82)
          const systemLinePrice = roundMoney2(
            entry.finalPrice ??
              resolvePathSystemLinePrice(entry, stockCalc.price)
          );

          const inventoryData = {
            name: entry.name,
            batchNumber: entry.batchNumber,
            // Convert packages → stock units (e.g. 2 bottles × 500 ml = 1000 ml)
            quantity: stockCalc.quantity,
            // System price = edited line total or package cost × package count
            price: systemLinePrice,
            vendorPrice: entry.vendorPrice,
            criticalLevel: resolvePathCriticalLevel(entry.criticalLevel),
            expirationDate: entry.expirationDate,
            userId: user.id,
            invoiceId: entry.invoiceId, // Link to the created invoice
            bomId: entry.bomId // Link to the selected BOM ID
          };
          const createdInventory = await createInventory(inventoryData);
          recordPathologyStockIn({
            inventoryId: createdInventory?.id,
            bomId: entry.bomId,
            itemName: entry.name,
            batchNumber: entry.batchNumber,
            unitType: bom?.unitType || bom?.qtyUnit || "",
            deductedQuantity: stockCalc.quantity,
            transactionType: "IN",
            invoiceId: entry.invoiceId,
            supplierId: supplierId || null,
            supplierName: values.supplierName,
            costPerUnit:
              stockCalc.quantity > 0
                ? systemLinePrice / stockCalc.quantity
                : systemLinePrice,
            expiryDate: entry.expirationDate,
            userId: user.id,
          });
          return createdInventory;
        });

        await Promise.all(productInventoryPromises);
        
        // Refresh data if callback is provided
        if (refreshData) {
          refreshData();
        }
        
        handelCloseModalState();
      } catch (error) {
        console.error("❌ Error submitting form:", error);
        alert(`Error creating inventory: ${error.message || "Please check all fields and try again"}`);
      }
    },
  });

  const fetchBOMs = async () => {
    try {
      // Fetch BOMs from base table (not organization-specific)
      // No need to filter by organizationId since BOMs are from base table
      let data = await getBOMsFromBaseTable();
      
      setBOMList(data);
    } catch (error) {
      console.log(error);
    }
  };

  const generateBatchNumber = () => {
    return 'PD' + Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  };

  useEffect(() => {
    fetchBOMs();
  }, []);

  // Refresh BOM list when modal opens or when refreshData is called
  useEffect(() => {
    if (open) {
      fetchBOMs();
      const loadSuppliers = async () => {
        try {
          const list = await getAllSuppliers();
          setSuppliersList(Array.isArray(list) ? list : []);
        } catch (error) {
          console.warn("[AddNewPurchaseInvoice] Could not load suppliers:", error);
          setSuppliersList([]);
        }
      };
      loadSuppliers();
    }
  }, [open, refreshData]);

  const handleSupplierKeyChange = (key) => {
    setSelectedSupplierKey(key);
    formik.setFieldTouched("supplierName", true, false);
    if (!key) {
      setIsNewSupplier(false);
      formik.setFieldValue("supplierName", "");
      formik.setFieldValue("supplierContact", "");
      formik.setFieldValue("supplierGstNumber", "");
      formik.setFieldValue("supplierAddress", "");
      return;
    }
    if (key === ADD_NEW_SUPPLIER_VALUE) {
      setIsNewSupplier(true);
      formik.setFieldValue("supplierName", "");
      formik.setFieldValue("supplierContact", "");
      formik.setFieldValue("supplierGstNumber", "");
      formik.setFieldValue("supplierAddress", "");
      return;
    }
    setIsNewSupplier(false);
    const selected = suppliersList.find((s) => String(s.id) === String(key));
    if (selected) {
      formik.setFieldValue("supplierName", selected.name || selected.supplierName || "");
      formik.setFieldValue("supplierContact", selected.contact || "");
      formik.setFieldValue("supplierGstNumber", selected.gstNumber || "");
      formik.setFieldValue("supplierAddress", selected.address || "");
      formik.setFieldError("supplierName", undefined);
      formik.setFieldError("supplierContact", undefined);
      formik.setFieldError("supplierGstNumber", undefined);
      formik.setFieldError("supplierAddress", undefined);
    }
  };

  // Fetch organization name and GST percentage when modal opens
  useEffect(() => {
    const fetchOrganizationNameAndGST = async () => {
      if (open) {
        try {
          // First try to get from user object or localStorage
          let orgName = getOrganizationName(user);
          const organizationId = getOrganizationId(user);
          
          // If not found, try to fetch it
          if (!orgName && organizationId && organizationId > 0) {
            orgName = await fetchAndStoreOrganizationName(organizationId);
          }
          
          // Set organization name, or keep default "Aryan Hospital" for basic users
          setOrganizationName(orgName || "Aryan Hospital");
          
          // Fetch active subscription to get GST percentage
          if (organizationId && organizationId > 0) {
            try {
              const activeSubscription = await getActiveSubscription(organizationId);
              if (activeSubscription && activeSubscription.gstPercentage) {
                setGstPercentage(parseFloat(activeSubscription.gstPercentage));
                console.log(`[AddNewPurchaseInvoice] Fetched GST percentage from subscription: ${activeSubscription.gstPercentage}%`);
              } else {
                // Default to 8.5% (4.25% CGST + 4.25% SGST) if no GST in subscription
                setGstPercentage(8.5);
                console.log(`[AddNewPurchaseInvoice] No GST in subscription, using default 8.5%`);
              }
            } catch (gstError) {
              console.warn(`[AddNewPurchaseInvoice] Error fetching GST from subscription:`, gstError);
              // Default to 8.5% on error
              setGstPercentage(8.5);
            }
          } else {
            // Default to 8.5% if no organization ID
            setGstPercentage(8.5);
          }
        } catch (error) {
          console.error(`[AddNewPurchaseInvoice] Error fetching organization name:`, error);
          // Keep default "Aryan Hospital" on error
          setOrganizationName("Aryan Hospital");
          setGstPercentage(8.5);
        }
      } else if (!open) {
        // Reset organization name and GST when modal closes
        setOrganizationName("Aryan Hospital");
        setGstPercentage(null);
      }
    };

    fetchOrganizationNameAndGST();
  }, [open, user]);

  const handleViewBatchNumbers = (index) => {
  setSelectedEntryIndex(index);
  setShowBatchNumbersModal(true);
};

  const handelCloseModalState = () => {
    onClose();
    formik.resetForm();
    setProductEntries([{
      name: "",
      batchNumber: "",
      quantity: '',
      price: '',
      systemPriceOverride: null,
      vendorPrice: '',
      expirationDate: null,
      criticalLevel: 10,
      existingBatchNumbers: []
    }]);
    // Reset supplier fields
    formik.setFieldValue('supplierName', '');
    formik.setFieldValue('supplierAddress', '');
    formik.setFieldValue('supplierContact', '');
    formik.setFieldValue('supplierGstNumber', '');
    formik.setFieldValue('totalDiscount', '');
    formik.setFieldValue('discount', 0);
    formik.setFieldValue('sgst', '');
    formik.setFieldValue('cgst', '');
    setSelectedSupplierKey("");
    setIsNewSupplier(false);
    setSelectedEntryIndex(null);
    // Reset touched and error states
    setTouchedProductEntries([{ expirationDate: false }]);
    setProductEntryErrors([]);
    setErrorUpdateTrigger(0);
  };

  const addProductEntry = () => {
    setProductEntries([...productEntries, {
      name: "",
      batchNumber: generateBatchNumber(),
      quantity: 0,
      price: 0,
      systemPriceOverride: null,
      vendorPrice: "",
      expirationDate: null,
      criticalLevel: 10,
      existingBatchNumbers: []
    }]);
    // Add touched state for new entry
    setTouchedProductEntries([...touchedProductEntries, { expirationDate: false }]);
    // Add error state for new entry
    setProductEntryErrors([...productEntryErrors, {}]);
  };

  const removeProductEntry = (index) => {
    const newEntries = [...productEntries];
    newEntries.splice(index, 1);
    setProductEntries(newEntries);
    
    // Remove touched state for deleted entry
    const newTouched = [...touchedProductEntries];
    newTouched.splice(index, 1);
    setTouchedProductEntries(newTouched);
    
    // Remove error state for deleted entry
    const newErrors = [...productEntryErrors];
    newErrors.splice(index, 1);
    setProductEntryErrors(newErrors);
  };

  /** Package unit cost from path item only — never use displayed (cost×qty) total. */
  const resolveEntryPackageCost = (entry, bom) => {
    const fromEntry = parseFloat(entry?.packageCost);
    if (Number.isFinite(fromEntry) && fromEntry > 0) return fromEntry;
    return getBomPackageCost(bom);
  };

  const resolveEntryPackageQty = (entry, index) =>
    parseFloat(formik.values[`quantity-${index}`] ?? entry?.quantity) || 0;

  const handleProductSelect = async (index, newValue) => {
    const newEntries = [...productEntries];
    // Clear batch number and existing batch numbers if no product is selected
    if (!newValue) {
      newEntries[index].name = "";
      newEntries[index].bomId = null;
      newEntries[index].batchNumber = "PD001"; // Default batch number
      newEntries[index].existingBatchNumbers = []; // Clear existing batch numbers
      setProductEntries(newEntries);
      formik.setFieldValue(`quantity-${index}`, "");
      formik.setFieldValue(`price-${index}`, "");
      return; // Exit early if no product is selected
    }

    newEntries[index].name = newValue;
    
    const selectedBOM = bomList.find(bom => bom.name === newValue);
    if (selectedBOM) {
      newEntries[index].bomId = selectedBOM.id; // Capture the BOM ID
      newEntries[index].name = selectedBOM.name;

      // Store package unit cost only (e.g. 202). System price field shows unit × qty.
      const packageCost = getBomPackageCost(selectedBOM);
      newEntries[index].packageCost = packageCost;
      newEntries[index].price = packageCost;
      newEntries[index].systemPriceOverride = null;
      formik.setFieldValue(`price-${index}`, packageCost);

      // Fetch existing batch numbers for the selected BOM
    try {
      const existingBatchNumbers = await getExistingBatchNumbers(selectedBOM.id);
      newEntries[index].existingBatchNumbers = existingBatchNumbers; // Store existing batch numbers
      // Generate the next batch number
      if (existingBatchNumbers.length > 0) {
        const sortedBatchNumbers = existingBatchNumbers.map(item => item.batchNumber).sort();
        const lastBatchNumber = sortedBatchNumbers[sortedBatchNumbers.length - 1];
        newEntries[index].batchNumber = generateNextBatchNumber(lastBatchNumber);
      } else {
        newEntries[index].batchNumber = "PD001"; // Start from PD001 if no existing batch numbers
      }
    } catch (error) {
      console.error("Failed to fetch existing batch numbers:", error);
      newEntries[index].batchNumber = "PD001"; // Default to PD001 if there's an error
      newEntries[index].existingBatchNumbers = []; // Clear existing batch numbers
    }
  } else {
    // If no BOM is found, clear the batch number and existing batch numbers
    newEntries[index].bomId = null;
    newEntries[index].batchNumber = "PD001"; // Default batch number
    newEntries[index].existingBatchNumbers = []; // Clear existing batch numbers
    }
    
    setProductEntries(newEntries);
  };

 // Function to generate the next batch number
  const generateNextBatchNumber = (lastBatchNumber) => {
    if (!lastBatchNumber) return "PD001"; // Default if no last batch number
    const numericPart = lastBatchNumber.replace(/\D/g, ''); // Extract numeric part
    const nextNumber = parseInt(numericPart, 10) + 1; // Increment the number
    return `PD${nextNumber.toString().padStart(3, '0')}`; // Format to PD### 
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAttachment(file);
    formik.setFieldValue("attachment", file.name);
    formik.setFieldError("attachment", undefined);
    formik.setFieldTouched("attachment", true, false);
  };

  const handleDeleteAttachment = () => {
    setAttachment(null);
    formik.setFieldValue("attachment", "");
    formik.setFieldError("attachment", "Attachment is required");
    formik.setFieldTouched("attachment", true, false);
  };

  // New function to calculate total cost
  const calculateTotalCost = (overrides = {}) => {
    // Use the latest values passed in (from onChange) and fall back to Formik values
    const actualCostRaw =
      overrides.actualCost !== undefined
        ? overrides.actualCost
        : formik.values.actualCost;
    const discountRaw =
      overrides.discount !== undefined
        ? overrides.discount
        : formik.values.discount;

    const actualCost = parseFloat(actualCostRaw) || 0; // Get the actual cost entered by the user
    const discount = parseFloat(discountRaw) || 0;

    // Calculate the amount after discount
    const amountAfterDiscount = actualCost - discount;

    // Use GST percentage from subscription if available, otherwise default to 8.5% (4.25% CGST + 4.25% SGST)
    const taxRate = gstPercentage !== null ? gstPercentage : 8.5;
    const tax = (amountAfterDiscount * taxRate) / 100; // Total tax
    const sgst = tax / 2; // SGST is half of the total tax (4.25% if default, or half of subscription GST)
    const cgst = tax / 2; // CGST is half of the total tax (4.25% if default, or half of subscription GST)

    // Calculate the final total cost
    const finalTotalCost = amountAfterDiscount + tax; // Total cost including tax

    // Set the values in the formik state
    formik.setFieldValue('sgst', sgst); // Set SGST
    formik.setFieldValue('cgst', cgst); // Set CGST
    formik.setFieldValue('totalCost', finalTotalCost); // Set total cost
  };

  const calculateInvoiceTax = (amount) => {
    // Use GST percentage from subscription if available, otherwise default to 8.5% (4.25% CGST + 4.25% SGST)
    const taxRate = gstPercentage !== null && gstPercentage > 0 ? gstPercentage : 8.5;
    const tax = (amount * taxRate) / 100; // Total tax
    const sgst = tax / 2; // SGST is half of the total tax (4.25% if default, or half of subscription GST)
    const cgst = tax / 2; // CGST is half of the total tax (4.25% if default, or half of subscription GST)
    const totalAmount = amount + tax; // Total amount including tax

    return {
      amount: amount, // Set the amount
      sgst: Math.round(sgst * 100) / 100, // Set SGST (rounded to 2 decimal places)
      cgst: Math.round(cgst * 100) / 100, // Set CGST (rounded to 2 decimal places)
      totalAmount: Math.round(totalAmount * 100) / 100, // Set total amount (rounded to 2 decimal places)
    };
  };

  return (
    <>
      <BasicModal
        open={open}
        onClose={handelCloseModalState}
        title="Add Stock"
        sx={NO_INNER_SCROLL_FORM_MODAL_SX}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX, p: 0 }}
        >
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <StockAddInvoiceSupplierSection
                formik={formik}
                invoiceTypeOptions={[
                  { value: "Pathology", label: "Pathology" },
                ]}
                suppliers={suppliersList}
                selectedSupplierKey={selectedSupplierKey}
                onSupplierKeyChange={handleSupplierKeyChange}
                isNewSupplier={isNewSupplier}
                errorUpdateTrigger={errorUpdateTrigger}
                onFileChange={handleFileChange}
                onDeleteAttachment={handleDeleteAttachment}
                attachmentFile={attachment}
                showAttachment={true}
              />

              <Divider sx={{ mt: 0.5, mb: 0.5 }} />

              {/* Section 2: Product Details */}
              <Typography variant="subtitle1" fontWeight={"bold"} pb={0.25}>
                Product Details
              </Typography>
              
              {productEntries.map((entry, index) => (
                <Grid container spacing={1} key={index} sx={{ mb: 0.5 }}>
                  <Grid item xs={2.5}>
                    <Autocomplete
                      options={bomList.map(bom => bom.name)}
                      value={entry.name || ""} // Ensure it defaults to an empty string
                      onChange={(e, newValue) => handleProductSelect(index, newValue)}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Select Product" 
                          variant="outlined" size="small" 
                          error={formik.touched[`name-${index}`] && Boolean(formik.errors.productEntries?.[index]?.name)} 
                          helperText={formik.touched[`name-${index}`] && formik.errors.productEntries?.[index]?.name} 
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={1.7}>
                    <TextField
                      fullWidth
                    size="small"
                      label="Batch Number"
                      value={entry.batchNumber}
                      onChange={(e) => {
                        const newEntries = [...productEntries];
                        newEntries[index].batchNumber = e.target.value;
                        setProductEntries(newEntries);
                      }}
                      // error={formik.touched[`batchNumber-${index}`] && Boolean(formik.errors.productEntries?.[index]?.batchNumber)}
                      // helperText={formik.touched[`batchNumber-${index}`] && formik.errors.productEntries?.[index]?.batchNumber}
                      disabled
                    />
                    {/* Display view existing batches in a Link component */}
                       {entry.existingBatchNumbers && entry.existingBatchNumbers.length > 0 && (
                        <Typography variant="caption" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          <Link 
                            component="button"
                            variant="caption"
                            onClick={() => {
                              setShowBatchNumbersModal(true);
                              setSelectedEntryIndex(index); // Set the selected entry index
                            }}
                            sx={{
                              color: 'text.secondary'
                            }}
                          >
                            View existing batches ({entry.existingBatchNumbers.length})
                          </Link>
                        </Typography>
                      )}
                  </Grid>

                  <Grid item xs={1.7}>
                  <TextField
                    fullWidth
                    size="small"
                    id={`quantity-${index}`}
                    name={`quantity-${index}`}
                    label="Quantity"
                    type="number"
                    value={formik.values[`quantity-${index}`] ?? entry.quantity ?? ""}
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : 0;
                      formik.setFieldValue(`quantity-${index}`, value);
                      const newEntries = [...productEntries];
                      newEntries[index].quantity = value;
                      // Recalculate System Price from package cost × qty after qty change
                      newEntries[index].systemPriceOverride = null;
                      setProductEntries(newEntries);
                    }}
                    error={formik.touched[`quantity-${index}`] && Boolean(formik.errors.productEntries?.[index]?.quantity)}
                    helperText={
                      (formik.touched[`quantity-${index}`] &&
                        formik.errors.productEntries?.[index]?.quantity) ||
                      (entry.bomId
                        ? getPathStockQuantityCaption(
                            formik.values[`quantity-${index}`] ?? entry.quantity,
                            bomList.find((b) => b.id === entry.bomId)
                          ) || undefined
                        : undefined)
                    }
                    FormHelperTextProps={{
                      sx: {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                        fontSize: "0.7rem",
                        m: 0,
                        mt: 0.25,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={1.7}>
                  {(() => {
                    const bom = bomList.find((b) => b.id === entry.bomId);
                    const packageQty = resolveEntryPackageQty(entry, index);
                    const packageCost = resolveEntryPackageCost(entry, bom);
                    // Single multiply only: unit package cost × package count
                    const calculatedSystemPrice =
                      Math.round(packageCost * packageQty * 100) / 100;
                    const displaySystemPrice = resolvePathSystemLinePrice(
                      entry,
                      calculatedSystemPrice
                    );
                    const unitPriceCaption = bom
                      ? getPathStockUnitPriceCaption(bom, packageCost)
                      : packageCost > 0
                        ? `unit price: ${packageCost}`
                        : "";

                    return (
                      <TextField
                        fullWidth
                        size="small"
                        id={`system-price-display-${index}`}
                        label="System Price"
                        type="number"
                        inputProps={{ min: 0, step: "0.01" }}
                        value={
                          entry.systemPriceOverride !== null &&
                          entry.systemPriceOverride !== undefined
                            ? entry.systemPriceOverride
                            : packageQty > 0 && Number.isFinite(calculatedSystemPrice)
                              ? calculatedSystemPrice
                              : packageCost > 0
                                ? packageCost
                                : ""
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (!isDecimalAmountInput(raw)) return;
                          const newEntries = [...productEntries];
                          newEntries[index].systemPriceOverride = raw;
                          setProductEntries(newEntries);
                        }}
                        error={
                          Boolean(productEntryErrors[index]?.price) ||
                          (formik.touched[`price-${index}`] &&
                            Boolean(formik.errors.productEntries?.[index]?.price))
                        }
                        helperText={
                          productEntryErrors[index]?.price ||
                          (formik.touched[`price-${index}`] &&
                            formik.errors.productEntries?.[index]?.price) ||
                          (unitPriceCaption
                            ? `${unitPriceCaption}${
                                packageQty > 1
                                  ? ` × ${packageQty} = ${displaySystemPrice}`
                                  : ""
                              }`
                            : undefined)
                        }
                        FormHelperTextProps={{
                          sx: {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: 1.25,
                            fontSize: "0.7rem",
                            m: 0,
                            mt: 0.25,
                          },
                        }}
                      />
                    );
                  })()}
                </Grid>

                <Grid item xs={1.7}>
                  <TextField
                    fullWidth
                    size="small"
                    id={`vendor-price-${index}`}
                    name={`vendorPrice-${index}`} // Unique name for each field
                    label="Supplier Amount"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={
                      formik.values[`vendorPrice-${index}`] ??
                      entry.vendorPrice ??
                      ""
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (!isDecimalAmountInput(raw)) return;
                      formik.setFieldValue(`vendorPrice-${index}`, raw);
                      const newEntries = [...productEntries];
                      newEntries[index].vendorPrice = raw;
                      setProductEntries(newEntries);
                    }}
                    error={
                      Boolean(productEntryErrors[index]?.vendorPrice) ||
                      (formik.touched[`vendorPrice-${index}`] &&
                        Boolean(formik.errors.productEntries?.[index]?.vendorPrice))
                    }
                    helperText={
                      productEntryErrors[index]?.vendorPrice ||
                      (formik.touched[`vendorPrice-${index}`] &&
                        formik.errors.productEntries?.[index]?.vendorPrice)
                    }
                  />
                </Grid>

                  <Grid item xs={2}>
                    <FormControl fullWidth error={touchedProductEntries[index]?.expirationDate && Boolean(productEntryErrors[index]?.expirationDate)}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Expiry Date *"
                          value={entry.expirationDate || null}
                          minDate={dayjs()} // Prevent past dates
                          onChange={(value) => {
                            const newEntries = [...productEntries];
                            newEntries[index].expirationDate = value || null;
                            setProductEntries(newEntries);
                            
                            // Mark expiry date as touched
                            const newTouched = [...touchedProductEntries];
                            if (!newTouched[index]) {
                              newTouched[index] = {};
                            }
                            newTouched[index].expirationDate = true;
                            setTouchedProductEntries(newTouched);
                            
                            // Set or clear error based on value
                            const newErrors = [...productEntryErrors];
                            if (!newErrors[index]) {
                              newErrors[index] = {};
                            }
                            if (value) {
                              // Clear error if value is set
                              delete newErrors[index].expirationDate;
                            } else {
                              // Set error if empty
                              newErrors[index].expirationDate = "Expiration date is required";
                            }
                            setProductEntryErrors(newErrors);
                            setErrorUpdateTrigger(prev => prev + 1);
                          }}
                          onClose={() => {
                            // Mark expiry date as touched when picker closes
                            const newTouched = [...touchedProductEntries];
                            if (!newTouched[index]) {
                              newTouched[index] = {};
                            }
                            newTouched[index].expirationDate = true;
                            setTouchedProductEntries(newTouched);
                            
                            // Set error immediately if empty
                            if (!entry.expirationDate) {
                              const newErrors = [...productEntryErrors];
                              if (!newErrors[index]) {
                                newErrors[index] = {};
                              }
                              newErrors[index].expirationDate = "Expiration date is required";
                              setProductEntryErrors(newErrors);
                              setErrorUpdateTrigger(prev => prev + 1);
                            }
                          }}
                          renderInput={(params) => {
                            const hasError = touchedProductEntries[index]?.expirationDate && Boolean(productEntryErrors[index]?.expirationDate);
                            return (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                                required
                                error={hasError}
                                helperText={hasError ? productEntryErrors[index]?.expirationDate : undefined}
                                onBlur={(e) => {
                                  params.onBlur?.(e);
                                  // Mark as touched and validate
                                  const newTouched = [...touchedProductEntries];
                                  if (!newTouched[index]) {
                                    newTouched[index] = {};
                                  }
                                  newTouched[index].expirationDate = true;
                                  setTouchedProductEntries(newTouched);
                                  
                                  // Set error immediately if empty
                                  if (!entry.expirationDate) {
                                    const newErrors = [...productEntryErrors];
                                    if (!newErrors[index]) {
                                      newErrors[index] = {};
                                    }
                                    newErrors[index].expirationDate = "Expiration date is required";
                                    setProductEntryErrors(newErrors);
                                    setErrorUpdateTrigger(prev => prev + 1);
                                  }
                                }}
                              />
                            );
                          }}
                          key={`expiry-date-${index}-${errorUpdateTrigger}`}
                        />
                      </LocalizationProvider>
                      {touchedProductEntries[index]?.expirationDate && productEntryErrors[index]?.expirationDate && (
                        <FormHelperText>{productEntryErrors[index].expirationDate}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={0.7}>
                    <IconButton onClick={() => removeProductEntry(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              <Button
                size="small"
                variant="outlined"
                onClick={addProductEntry}
                sx={{ mt: 0.5 }}
              >
                Add More
              </Button>

              {productEntries.length > 0 && (
                <Box sx={{ mt: 0.75, mb: 0.25 }}>
                  <Divider sx={{ mb: 1 }} />
                  {(() => {
                    const totalSupplierPrice = productEntries.reduce(
                      (sum, entry, index) => {
                        const supplierAmount =
                          parseFloat(
                            formik.values[`vendorPrice-${index}`] ??
                              entry.vendorPrice
                          ) || 0;
                        return sum + supplierAmount;
                      },
                      0
                    );
                    const discount =
                      parseFloat(formik.values.totalDiscount) || 0;
                    const subtotal = Math.max(0, totalSupplierPrice - discount);
                    const { totalAmount } = calcManualSgstCgstTotal(
                      subtotal,
                      formik.values.sgst,
                      formik.values.cgst
                    );
                    return (
                      <InvoiceSummarySection
                        subtotal={subtotal}
                        manualGst
                        showDiscountInGstRow
                        discountValue={formik.values.totalDiscount}
                        onDiscountChange={(v) => {
                          formik.setFieldValue("totalDiscount", v);
                          formik.setFieldValue("discount", v);
                        }}
                        sgstValue={formik.values.sgst}
                        cgstValue={formik.values.cgst}
                        onSgstChange={(v) => formik.setFieldValue("sgst", v)}
                        onCgstChange={(v) => formik.setFieldValue("cgst", v)}
                        totalAmount={totalAmount}
                        paymentModeValue={formik.values.payment_mode}
                        onPaymentModeChange={(value) =>
                          formik.setFieldValue("payment_mode", value)
                        }
                        remarksName="particulars"
                        remarksValue={formik.values.particulars || ""}
                        onRemarksChange={formik.handleChange}
                      />
                    );
                  })()}
                </Box>
              )}

              <Grid item xs={12} mt={0.5}>
                <FormModalActions
                  onCancel={handelCloseModalState}
                  submitType="submit"
                  submitDisabled={formik.isSubmitting}
                  loading={formik.isSubmitting}
                />
              </Grid>
            </form>
          </Box>
        </Box>
      </BasicModal>

<BasicModal
  open={showBatchNumbersModal}
  onClose={() => setShowBatchNumbersModal(false)}
  sx={COMPACT_FORM_MODAL_SX}
>
  <Typography variant="h6" gutterBottom>
    Existing Batch Numbers
  </Typography>
  <List sx={{ maxHeight: '300px', overflowY: 'auto', padding: 0 }}>
    {selectedEntryIndex !== null && productEntries[selectedEntryIndex] && productEntries[selectedEntryIndex].existingBatchNumbers ? (
      productEntries[selectedEntryIndex].existingBatchNumbers.length > 0 ? (
        productEntries[selectedEntryIndex].existingBatchNumbers.map((item, i) => (
          <ListItem key={i} sx={{ padding: '4px 16px' }}>
            <ListItemText primary={`${item.batchNumber} - qty: ${item.quantity}`} />
          </ListItem>
        ))
      ) : (
        <ListItem>
          <ListItemText primary="No existing batch numbers available." />
        </ListItem>
      )
    ) : (
      <ListItem>
        <ListItemText primary="No existing batch numbers available." />
      </ListItem>
    )}
  </List>
</BasicModal>

    </>
  );
};

export default AddNewPurchaseInvoice;
